<!-- eslint-disable vue/v-on-event-hyphenation -->
<template>
  <v-container>
    <v-row
      no-gutters
      class="no-print"
    >
      <v-col>
        <v-menu
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              :value="dates.join(' ~ ')"
              :prepend-icon="icons.mdiCalendar"
              readonly
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>

          <v-date-picker
            v-model="dates"
            locale="ja"
            :day-format="date => new Date(date).getDate()"
            :min="isPeriodMin ? firstDayOfPreviousMonth() : undefined"
            color="primary"
            range
            no-title
          ></v-date-picker>
        </v-menu>
      </v-col>
    </v-row>

    <v-row
      no-gutters
      class="no-print"
    >
      <v-col
        cols="12"
        md="5"
      >
        <v-select
          v-model="selectedContainerId"
          :items="containers"
          item-text="attributes.name"
          item-value="attributes.id"
          placeholder="報酬明細を選択してください"
          hide-details
          no-data-text="取得可能な報酬明細がありません"
        >
          <template #append-outer>
            <div class="d-flex align-center">
              <v-menu
                bottom
                left
              >
                <template #activator="{ on: menuOn, attrs: menuAttrs }">
                  <v-btn
                    icon
                    :disabled="isLoading || loadingStatuses[selectedContainerId] || !selectedContainerId"
                    v-bind="menuAttrs"
                    v-on="menuOn"
                  >
                    <v-icon small>
                      {{ icons.mdiDotsVertical }}
                    </v-icon>
                  </v-btn>
                </template>

                <template #default>
                  <v-list
                    :disabled="isLoading || loadingStatuses[selectedContainerId]"
                    dense
                  >
                    <v-subheader>操作</v-subheader>
                    <PayrollContainerUsersDialogListItem
                      :container-id="selectedContainerId"
                      :assigned-users="assignedUsers || []"
                      :available-users="availableUsers || []"
                      @update="updateUsers"
                      @update:assignedUsers="assignedUsers = $event"
                      @update:availableUsers="availableUsers = $event"
                    />
                  </v-list>
                </template>
              </v-menu>

              <v-checkbox
                v-model="isToday"
                :ripple="false"
                label="当日出勤"
                dense
                hide-details
                class="ma-0 pa-0 ml-2 mr-4"
              />

              <v-btn
                color="primary"
                :disabled="!isValidSelection"
                class="ml-2"
                @click="getPayroll"
              >
                取得
              </v-btn>
            </div>
          </template>
        </v-select>
      </v-col>
    </v-row>

    <template v-if="loadingStatuses[selectedContainerId]">
      <v-row
        v-for="n in 10"
        :key="n"
        class="user-section"
      >
        <v-col>
          <v-skeleton-loader type="table-heading, list-item-two-line" />
        </v-col>
      </v-row>
    </template>

    <template v-else>
      <template v-if="containersMap[selectedContainerId]">
        <v-row
          v-for="user in containersMap[selectedContainerId]"
          :key="user.id"
          class="user-section"
        >
          <v-col>
            <PayrollContainer
              :container="user.attributes.payroll.data"
              :user="user"
              :start-date="dates[0]"
            />
          </v-col>
        </v-row>
      </template>
    </template>
  </v-container>
</template>

<script>
import Vue from 'vue'
import {
  ref,
  onMounted,
  reactive,
  inject,
  computed,
  getCurrentInstance,
  watch,
} from '@vue/composition-api'
import { useRouter } from '@core/utils'
import { parseISO, startOfMonth, format } from 'date-fns'
import { mdiCalendar, mdiDotsVertical } from '@mdi/js'
import PayrollContainer from './components/PayrollContainer.vue'
import PayrollContainerUsersDialogListItem from './components/PayrollContainerUsersDialogListItem.vue'
import PayrollAggregationContainerApi from '@/api/v2/employee/payroll/PayrollAggregationContainer'
import TimeApi from '@/api/v2/utility/Time'
import { handleApiError } from '@/utils/errorHandler'
import { useDateRange } from '@/views/v2-temp/common/composables/useDateRange'
import { useAbilityEvaluate } from '@/views/v2-temp/common/composables/useAbilityEvaluate'

export default {
  components: {
    PayrollContainer,
    PayrollContainerUsersDialogListItem,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const currentClubMeta = inject('currentClubMeta', { today: null })
    const { route } = useRouter()
    const today = currentClubMeta.today || new Date().toISOString().slice(0, 10)
    const date = ref(route.value.query.date || today)
    const startDate = ref(format(startOfMonth(parseISO(date.value)), 'yyyy-MM-dd'))
    const dates = ref([startDate.value, date.value])
    const isLoading = ref(false)

    const interval = ref('monthly')
    const isToday = ref(false)

    const containers = ref([])
    const containersMap = ref({})
    const assignedUsers = ref([])
    const availableUsers = ref([])
    const selectedContainerId = ref(null)
    const loadingStatuses = reactive({})

    const isValidSelection = computed(() => {
      return selectedContainerId.value && dates.value.length === 2 && dates.value[0] && dates.value[1]
    })

    const { canAccessPeriod } = useAbilityEvaluate()
    const { firstDayOfPreviousMonth } = useDateRange()
    const isPeriodMin = computed(() => !canAccessPeriod())

    const getPayrollContainers = async () => {
      try {
        isLoading.value = true
        const res = await PayrollAggregationContainerApi.getPayrollContainers()
        if (res.data.status === 'success') {
          const { data } = res.data.data
          containers.value = data
          data.forEach(async cr => {
            Vue.set(loadingStatuses, cr.attributes.id, false)
          })
        }
      } finally {
        isLoading.value = false
      }
    }

    const getPayroll = async () => {
      try {
        if (!isValidSelection.value) return

        Vue.set(loadingStatuses, selectedContainerId.value, true)
        const res = await PayrollAggregationContainerApi.getPayrollContainer(selectedContainerId.value, {
          date: dates.value[1],
          startDate: dates.value[0],
          interval: interval.value,
          isToday: isToday.value,
        })
        if (res.data.status === 'success') {
          const { data } = res.data.data
          containersMap.value = { ...containersMap.value, [selectedContainerId.value]: data }
        }
      } finally {
        Vue.set(loadingStatuses, selectedContainerId.value, false)
      }
    }

    const getAssignedUsers = async () => {
      try {
        const res = await PayrollAggregationContainerApi.getAssignedUsers(selectedContainerId.value)
        if (res.data.status === 'success') {
          assignedUsers.value = res.data.data.data
        }
      } catch (error) {
        handleApiError(error, { $toast: vm.$toast })
      }
    }
    const getAvailableUsers = async () => {
      try {
        const res = await PayrollAggregationContainerApi.getAvailableUsers(selectedContainerId.value)
        if (res.data.status === 'success') {
          availableUsers.value = res.data.data.data
        }
      } catch (error) {
        handleApiError(error, { $toast: vm.$toast })
      }
    }

    const updateUsers = async ({ users, callback }) => {
      try {
        const userIds = users.map(user => user.id)
        await PayrollAggregationContainerApi.updateUsers(selectedContainerId.value, userIds)
      } catch (error) {
        handleApiError(error, { $toast: vm.$toast })
      } finally {
        callback()
      }
    }

    const getToday = async () => {
      const res = await TimeApi.getToday()

      if (res.data.status === 'success') {
        date.value = res.data.data
      }
    }

    watch(selectedContainerId, async () => {
      if (selectedContainerId.value) {
        Promise.all([
          getAssignedUsers(),
          getAvailableUsers(),
        ])
      }
    })

    onMounted(() => {
      getPayrollContainers()
      getToday()
    })

    return {
      containers,
      containersMap,
      selectedContainerId,
      loadingStatuses,
      assignedUsers,
      availableUsers,
      getPayroll,
      interval,
      dates,
      isLoading,
      isValidSelection,
      updateUsers,
      isPeriodMin,
      firstDayOfPreviousMonth,
      isToday,
      icons: {
        mdiCalendar,
        mdiDotsVertical,
      },
    }
  },
}
</script>

<style scoped>
::v-deep .v-input--checkbox .v-label {
  position: static !important;
  left: auto !important;
  right: auto !important;
}
</style>
